import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { toast, ToastContainer } from 'react-toastify'
import { showErrorToast } from '../../../shared/utils/apiErrorMessage'
import { KTSVG } from '../../../../_metronic/helpers'
import { useAuth } from '../../auth'
import { Crew, Rank, Company, CompanyAdmin, Vessel } from '../core/_models'
import {
  AppraisalAttachmentType,
  AppraisalType,
  AverageReport,
  CrewAppraisal,
  AppraisalWorkflowStatus,
  ReviewDepartment,
  CreateCrewAppraisalRequest,
  UpdateCrewAppraisalRequest,
  createCrewAppraisal,
  deleteCrewAppraisal,
  getCompanyAdminList,
  getCompanyList,
  getCrewAppraisals,
  getCrewList,
  getRanksforList,
  submitCrewAppraisal,
  submitDepartmentReview,
  updateCrewAppraisal,
  uploadCrewAppraisalAttachment,
} from '../core/_requests'
import CrewAppraisalModal, { PendingAppraisalAttachment } from '../components/CrewAppraisalModal'
import ViewCrewAppraisalModal from '../components/ViewCrewAppraisalModal'
import CrewAppraisalReviewModal from '../components/CrewAppraisalReviewModal'
import CrewAppraisalAttachmentsModal from '../components/CrewAppraisalAttachmentsModal'
import { FileViewerModal } from '../../QHSE/components/FileViewerModal'
import { RemarksCell, RemarksModal } from '../../QHSE/components/StructuredRemarksView'
import {
  CREWING_ACTION_PERMISSIONS,
  CREWING_READ_PERMISSIONS,
  useCrewingCanRead,
  useCrewingPermissionRevision,
} from '../core/crewingPermissionAccess'
import { useCrewingRoleAccess } from '../core/crewingRoleAccess'
import {
  applyCrewingPresentationFilters,
  allowCompanyPresentationFilters,
  extractRows,
} from '../core/crewingPresentationFilters'
import { fetchAccessibleVesselsForCurrentUser, normalizeAccessibleVessels } from '../../../shared/access/accessibleVessels'
import { TableSkeleton } from '../../../shared/components/LoadingSkeletons'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext'
import {
  resolveVesselCompanyAdminId,
  resolveVesselCompanyGroupId,
} from '../../../shared/access/tenantScopeMatch'

type SortKey = keyof CrewAppraisal | 'srNo'
type SortConfig = { key: SortKey; direction: 'asc' | 'desc' }

const APPRAISAL_TYPE_LABEL: Record<AppraisalType, string> = {
  MID_TERM: 'Mid Term',
  HOD_SIGNOFF: 'HOD Signoff',
  SPECIAL_CASE: 'Special Case',
}

const AVERAGE_REPORT_LABEL: Record<AverageReport, string> = {
  NTBE: 'NTBE',
  AVERAGE: 'Average',
  GOOD: 'Good',
  EXCELLENT: 'Excellent',
}

const AVERAGE_BADGE_CLASS: Record<AverageReport, string> = {
  NTBE: 'badge-light-danger',
  AVERAGE: 'badge-light-warning',
  GOOD: 'badge-light-success',
  EXCELLENT: 'badge-success',
}

const DEFAULT_APPRAISAL_TYPE: AppraisalType = 'MID_TERM'
const DEFAULT_AVERAGE_REPORT: AverageReport = 'AVERAGE'

const Appraisal: FC = () => {
  const { currentUser, auth, hasPermission, isSuperAdmin } = useAuth()
  const {
    isCrew,
    isOfficeRole,
    isCompanyGroupAdmin,
    isCompanyAdmin: baseIsCompanyAdmin,
    isOperator,
  } = useCrewingRoleAccess()
  const roleId = currentUser?.role?.id

  const operatorCompanyAdminId = Number((currentUser as any)?.companyAdminId ?? (currentUser as any)?.companyAdmin?.id ?? 0) || undefined
  const isCompanyAdmin = baseIsCompanyAdmin || (isOperator && !!operatorCompanyAdminId)
  const crewRankName = ((currentUser as any)?.rank?.rank ?? '').toLowerCase()
  const crewRankId = Number((currentUser as any)?.rank?.id ?? 0)
  const isCrewMaster = isCrew && (crewRankId === 1 || crewRankName.includes('master'))
  const currentCrewId = isCrew ? Number((currentUser as any)?.roleEntityId ?? 0) : 0
  const roleEntityId = Number((currentUser as any)?.roleEntityId ?? 0)
  const currentUserId = Number((currentUser as any)?.id ?? (auth?.userDetails as any)?.id ?? 0)
  const currentUserUsername = String((currentUser as any)?.username ?? (auth?.userDetails as any)?.username ?? '').toLowerCase()
  const currentCrewUsername = (currentUser as any)?.username ?? ''
  const currentCrewName = (currentUser as any)?.roleEntityName ?? currentCrewUsername
  const masterDismissedAlertsStorageKey = `dismissedAppraisalAlerts:${currentUserId || currentUserUsername || 'anon'}`

  const showCompanyFilter = isSuperAdmin
  const showSubCompanyFilter = isSuperAdmin || isCompanyGroupAdmin
  const showVesselFilter = isSuperAdmin || isCompanyGroupAdmin || isCompanyAdmin || isOperator
  const useCrewDropdownFilter = isCompanyGroupAdmin || isCompanyAdmin
  const canCreate = isSuperAdmin || hasPermission('CREATE_CREW_APPRAISAL')
  const canReview = isSuperAdmin || (hasPermission('APPROVE_CREW_APPRAISAL') && isOfficeRole)
  const canSubmitAppraisal = isSuperAdmin || hasPermission('APPROVE_CREW_APPRAISAL')
  const canDelete = isSuperAdmin || hasPermission('DELETE_CREW_APPRAISAL')

  const fixedCompanyGroupId = useMemo(() => {
    if (isCompanyGroupAdmin) {
      return Number((currentUser as any)?.companyGroupAdminId ?? (currentUser as any)?.companyGroupAdmin?.id ?? (roleEntityId || 0)) || undefined
    }
    if (isCompanyAdmin) {
      return Number((currentUser as any)?.companyGroupAdminId ?? (currentUser as any)?.companyAdmin?.cga?.id ?? 0) || undefined
    }
    return undefined
  }, [currentUser, isCompanyGroupAdmin, isCompanyAdmin, roleEntityId])

  const fixedCompanyAdminId = useMemo(() => {
    if (!isCompanyAdmin) return undefined
    return Number((currentUser as any)?.companyAdminId ?? (currentUser as any)?.companyAdmin?.id ?? (roleEntityId || 0)) || undefined
  }, [currentUser, isCompanyAdmin, roleEntityId])

  const allowCompanyFilters = allowCompanyPresentationFilters(isSuperAdmin, roleId, isOperator)

  const canReadAppraisals = useCrewingCanRead(CREWING_READ_PERMISSIONS.APPRAISAL)
  const permissionRevision = useCrewingPermissionRevision()

  const [appraisals, setAppraisals] = useState<CrewAppraisal[]>([])
  const [ranks, setRanks] = useState<Rank[]>([])
  const [crewMembers, setCrewMembers] = useState<Crew[]>([])
  const [companies, setCompanies] = useState<Company[]>([])
  const [companyAdmins, setCompanyAdmins] = useState<CompanyAdmin[]>([])
  const [vessels, setVessels] = useState<Vessel[]>([])

  const [selectedCompanyGroupId, setSelectedCompanyGroupId] = useState<number | ''>('')
  const [selectedCompanyAdminId, setSelectedCompanyAdminId] = useState<number | ''>('')
  const [selectedVesselId, setSelectedVesselId] = useState<number | ''>('')

  const { state: globalState, vessels: globalVessels } = useGlobalFilter()

  useEffect(() => {
    setSearchTerm(globalState.searchQuery || '')
  }, [globalState.searchQuery])

  useEffect(() => {
    if (globalState.vessel?.id) {
      setSelectedVesselId(Number(globalState.vessel.id))
    } else if (isCrew && currentUser?.vessel?.id) {
      setSelectedVesselId(Number(currentUser.vessel.id))
    } else {
      setSelectedVesselId('')
    }

    if (globalState.company) {
      if (globalState.company.type === 'company_group') {
        setSelectedCompanyGroupId(Number(globalState.company.id))
        setSelectedCompanyAdminId('')
      } else {
        setSelectedCompanyAdminId(Number(globalState.company.id))
        setSelectedCompanyGroupId('')
      }
    } else {
      setSelectedCompanyGroupId(isCompanyGroupAdmin ? (fixedCompanyGroupId ?? '') : '')
      setSelectedCompanyAdminId(isCompanyAdmin ? (fixedCompanyAdminId ?? '') : '')
    }
  }, [
    globalState.vessel,
    globalState.company,
    isCrew,
    currentUser,
    isCompanyGroupAdmin,
    isCompanyAdmin,
    fixedCompanyGroupId,
    fixedCompanyAdminId,
  ])
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCrewId, setSelectedCrewId] = useState<number | ''>('')
  const [statusFilter, setStatusFilter] = useState<'All' | AppraisalWorkflowStatus>('All')
  const [averageFilter, setAverageFilter] = useState<'All' | AverageReport>('All')
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'crewName', direction: 'asc' })

  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isNewAppraisal, setIsNewAppraisal] = useState(false)
  const [editingAppraisal, setEditingAppraisal] = useState<CrewAppraisal | null>(null)
  const [isViewModalOpen, setIsViewModalOpen] = useState(false)
  const [viewAppraisal, setViewAppraisal] = useState<CrewAppraisal | null>(null)
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false)
  const [reviewingAppraisal, setReviewingAppraisal] = useState<CrewAppraisal | null>(null)
  const [isReviewSaving, setIsReviewSaving] = useState(false)
  const [attachmentsModal, setAttachmentsModal] = useState<{ visible: boolean; appraisal: CrewAppraisal | null }>({ visible: false, appraisal: null })
  const [fileModal, setFileModal] = useState<{ visible: boolean; title: string; fileName?: string; viewUrl?: string; downloadUrl?: string }>({ visible: false, title: '' })
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const didInitFiltersRef = useRef(false)
  const [dismissedAlertIds, setDismissedAlertIds] = useState<Set<number>>(new Set())
  const [dismissedReviewerAlertIds, setDismissedReviewerAlertIds] = useState<Set<number>>(() => {
    try {
      const stored = sessionStorage.getItem('dismissedReviewerAppraisalAlerts')
      return stored ? new Set(JSON.parse(stored)) : new Set()
    } catch { return new Set() }
  })
  const [remarksModal, setRemarksModal] = useState<{
    visible: boolean
    title: string
    subtitle: string
    text: string
  }>({
    visible: false,
    title: '',
    subtitle: '',
    text: '',
  })

  const fetchAppraisals = async () => {
    setIsLoading(true)
    try {
      setAppraisals(canReadAppraisals ? extractRows<CrewAppraisal>(await getCrewAppraisals()) : [])
    } catch (error) {
      console.error(error)
      showErrorToast(error, 'Failed to fetch appraisals', 'load')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    try {
      const stored = localStorage.getItem(masterDismissedAlertsStorageKey)
      setDismissedAlertIds(stored ? new Set(JSON.parse(stored)) : new Set())
    } catch {
      setDismissedAlertIds(new Set())
    }
  }, [masterDismissedAlertsStorageKey])

  useEffect(() => {
    if (!roleId || !canReadAppraisals) return
    fetchAppraisals()
    getRanksforList().then(setRanks).catch(console.error)
    getCrewList().then(setCrewMembers).catch(console.error)
    if (showSubCompanyFilter) getCompanyList().then(setCompanies).catch(console.error)
    if (showCompanyFilter) getCompanyAdminList().then(setCompanyAdmins).catch(console.error)
    if (showVesselFilter) fetchAccessibleVesselsForCurrentUser().then((list) => setVessels(normalizeAccessibleVessels(list))).catch(console.error)
  }, [roleId, canReadAppraisals, permissionRevision])

  useEffect(() => {
    if (didInitFiltersRef.current) return
    if (isCompanyGroupAdmin) {
      const cgaId = currentUser?.companyGroupAdminId ?? currentUser?.companyGroupAdmin?.id
      if (cgaId) setSelectedCompanyGroupId(cgaId)
    }
    if (isCompanyAdmin) {
      const caId = currentUser?.companyAdminId ?? currentUser?.companyAdmin?.id
      if (caId) setSelectedCompanyAdminId(caId)
    }
    if (isCrew) {
      const vesselId = currentUser?.vessel?.id
      if (vesselId) setSelectedVesselId(vesselId)
    }
    didInitFiltersRef.current = true
  }, [currentUser, isCompanyGroupAdmin, isCompanyAdmin, isCrew])

useEffect(() => {
  setCurrentPage(1)
}, [searchTerm, selectedCrewId, selectedCompanyGroupId, selectedCompanyAdminId, selectedVesselId, statusFilter, averageFilter])

const getVesselCompanyAdminId = (v: Vessel): number | undefined =>
  resolveVesselCompanyAdminId(v) ?? v.companyAdmin?.id ?? (v as any).companyAdminId ?? undefined
const getVesselCompanyGroupId = (v: Vessel): number | undefined =>
  resolveVesselCompanyGroupId(v) ?? (v as any).companyGroupAdminId ?? v.companyGroupAdmin?.id ?? (v.companyAdmin as any)?.cgaid?.id ?? v.companyAdmin?.cga?.id

const effectiveVessels = useMemo(() => {
  const map = new Map<number, Vessel>()
  ;(globalVessels || []).forEach((v) => {
    if (v?.id) map.set(Number(v.id), v)
  })
  ;(vessels || []).forEach((v) => {
    if (v?.id) map.set(Number(v.id), v)
  })
  return Array.from(map.values())
}, [globalVessels, vessels])

const vesselById = useMemo(() => new Map(effectiveVessels.map((v) => [v.id, v])), [effectiveVessels])
const selfCrewMember = useMemo(() => {
  if (!isCrew) return undefined
  if (currentCrewId) {
    const byId = crewMembers.find((member) => member.id === currentCrewId)
    if (byId) return byId
  }
  if (currentCrewUsername) {
    return crewMembers.find((member) => member.username === currentCrewUsername)
  }
  return undefined
}, [isCrew, currentCrewId, currentCrewUsername, crewMembers])

const selectableCrewMembers = useMemo(() => {
  if (!isCrew || isCrewMaster) return crewMembers
  return selfCrewMember ? [selfCrewMember] : []
}, [isCrew, isCrewMaster, crewMembers, selfCrewMember])

const masterReviewedAlerts = useMemo(() => {
  if (!isCrewMaster) return []
  return appraisals.filter((a) => {
    if (a.workflowStatus !== 'REVIEWED') return false
    const createdById = Number(a.createdByUserId ?? 0)
    const createdByUsername = String(a.createdByUsername ?? '').toLowerCase()
    const createdByMatches =
      (currentUserId > 0 && createdById > 0 && createdById === currentUserId) ||
      (!!currentUserUsername && !!createdByUsername && createdByUsername === currentUserUsername)
    if (!createdByMatches) return false
    if (dismissedAlertIds.has(a.id!)) return false
    return true
  })
}, [isCrewMaster, appraisals, currentUserId, currentUserUsername, dismissedAlertIds])

const reviewerPendingAlerts = useMemo(() => {
  if (!canReview) return []
  return appraisals.filter((a) => {
    if (a.workflowStatus !== 'SUBMITTED') return false
    if (dismissedReviewerAlertIds.has(a.id!)) return false
    return true
  })
}, [canReview, appraisals, dismissedReviewerAlertIds])



const crewFilterOptions = useMemo(() => {
  const uniqueById = new Map<number, string>()

  crewMembers.forEach((crew) => {
    const crewId = Number(crew.id)
    if (!Number.isFinite(crewId) || crewId <= 0) return

    const crewVesselId = crew.vesselId ?? crew.vessel?.id
    const crewVessel = crewVesselId ? vesselById.get(crewVesselId) : undefined
    const crewCompanyGroupId =
      (crew as any)?.companyGroupAdminId ??
      (crew as any)?.companyGroupAdmin?.id ??
      (crewVessel ? getVesselCompanyGroupId(crewVessel) : undefined)
    const crewCompanyAdminId =
      (crew as any)?.companyAdminId ??
      (crew as any)?.companyAdmin?.id ??
      (crewVessel ? getVesselCompanyAdminId(crewVessel) : undefined)

    const matchesCompanyGroup = fixedCompanyGroupId != null
      ? crewCompanyGroupId === fixedCompanyGroupId
      : !selectedCompanyGroupId || crewCompanyGroupId === Number(selectedCompanyGroupId)
    const matchesCompanyAdmin = fixedCompanyAdminId != null
      ? crewCompanyAdminId === fixedCompanyAdminId
      : !selectedCompanyAdminId || crewCompanyAdminId === Number(selectedCompanyAdminId)
    const matchesVessel = !selectedVesselId || crewVesselId === Number(selectedVesselId)

    if (!matchesCompanyGroup || !matchesCompanyAdmin || !matchesVessel) return
    uniqueById.set(crewId, crew.name || `Crew ${crewId}`)
  })

  if (uniqueById.size === 0) {
    appraisals.forEach((appraisal) => {
      const appraisalVessel = appraisal.vesselId ? vesselById.get(appraisal.vesselId) : undefined
      const appraisalCompanyGroupId = appraisalVessel ? getVesselCompanyGroupId(appraisalVessel) : undefined
      const appraisalCompanyAdminId = appraisalVessel ? getVesselCompanyAdminId(appraisalVessel) : undefined
      const matchesCompanyGroup = fixedCompanyGroupId != null
        ? appraisalCompanyGroupId === fixedCompanyGroupId
        : !selectedCompanyGroupId || appraisalCompanyGroupId === Number(selectedCompanyGroupId)
      const matchesCompanyAdmin = fixedCompanyAdminId != null
        ? appraisalCompanyAdminId === fixedCompanyAdminId
        : !selectedCompanyAdminId || appraisalCompanyAdminId === Number(selectedCompanyAdminId)
      const matchesVessel = !selectedVesselId || appraisal.vesselId === Number(selectedVesselId)

      if (!matchesCompanyGroup || !matchesCompanyAdmin || !matchesVessel) return
      if (!uniqueById.has(appraisal.crewId)) {
        uniqueById.set(appraisal.crewId, appraisal.crewName || `Crew ${appraisal.crewId}`)
      }
    })
  }

  return Array.from(uniqueById.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name))
}, [crewMembers, appraisals, selectedCompanyGroupId, selectedCompanyAdminId, selectedVesselId, vesselById, fixedCompanyGroupId, fixedCompanyAdminId])

useEffect(() => {
  if (!selectedCrewId) return
  const exists = crewFilterOptions.some((crew) => crew.id === Number(selectedCrewId))
  if (!exists) setSelectedCrewId('')
}, [selectedCrewId, crewFilterOptions])

const filteredAppraisals = useMemo(() => {
  if (!canReadAppraisals) return []

  return applyCrewingPresentationFilters(appraisals, {
    searchTerm: useCrewDropdownFilter ? undefined : searchTerm,
    selectedCrewId: useCrewDropdownFilter ? selectedCrewId : undefined,
    selectedVesselId,
    allowCompanyPresentationFilters: allowCompanyFilters,
    selectedCompanyGroupId: allowCompanyFilters ? selectedCompanyGroupId : '',
    selectedCompanyAdminId: allowCompanyFilters ? selectedCompanyAdminId : '',
  }, {
    vesselById,
    extraPredicate: (appraisal) => {
      const typed = appraisal
      const term = searchTerm.trim().toLowerCase()
      const matchesCrew = !selectedCrewId || typed.crewId === Number(selectedCrewId)
      const matchesSearch =
        !term ||
        (typed.crewName || '').toLowerCase().includes(term) ||
        (typed.rankName || '').toLowerCase().includes(term) ||
        (typed.vesselName || '').toLowerCase().includes(term) ||
        (typed.appraisalType || '').toLowerCase().includes(term)
      const matchesStatus = statusFilter === 'All' || typed.workflowStatus === statusFilter
      const matchesAverage = averageFilter === 'All' || typed.averageReport === averageFilter
      return matchesCrew && matchesSearch && matchesStatus && matchesAverage
    },
  })
}, [
  appraisals,
  canReadAppraisals,
  searchTerm,
  selectedCrewId,
  statusFilter,
  averageFilter,
  selectedCompanyGroupId,
  selectedCompanyAdminId,
  selectedVesselId,
  vesselById,
  allowCompanyFilters,
  useCrewDropdownFilter,
])

const sortedAppraisals = useMemo(() => {
  const rows = [...filteredAppraisals]
  rows.sort((a, b) => {
    const first = a[sortConfig.key as keyof CrewAppraisal]
    const second = b[sortConfig.key as keyof CrewAppraisal]
    if (typeof first === 'number' && typeof second === 'number') return sortConfig.direction === 'asc' ? first - second : second - first
    return sortConfig.direction === 'asc' ? String(first || '').localeCompare(String(second || '')) : String(second || '').localeCompare(String(first || ''))
  })
  return rows
}, [filteredAppraisals, sortConfig])

const indexOfLast = currentPage * rowsPerPage
const indexOfFirst = indexOfLast - rowsPerPage
const currentRows = sortedAppraisals.slice(indexOfFirst, indexOfLast)
const totalPages = Math.ceil(sortedAppraisals.length / rowsPerPage)

const totals = useMemo(
  () => ({
    total: filteredAppraisals.length,
    submitted: filteredAppraisals.filter((a) => a.workflowStatus === 'SUBMITTED').length,
    inProgress: filteredAppraisals.filter((a) => a.workflowStatus === 'SUBMITTED' && (a.reviewCount ?? 0) > 0 && (a.reviewCount ?? 0) < 4).length,
    reviewed: filteredAppraisals.filter((a) => a.workflowStatus === 'REVIEWED').length,
    ntbe: filteredAppraisals.filter((a) => a.averageReport === 'NTBE').length,
  }),
  [filteredAppraisals]
)

const handleSort = (key: SortKey) => {
  setSortConfig((prev) => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }))
}

const handleTableHorizontalWheelScroll = (event: React.WheelEvent<HTMLDivElement>) => {
  const container = event.currentTarget
  if (container.scrollWidth <= container.clientWidth) return
  if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) return

  const canScrollDown = container.scrollTop + container.clientHeight < container.scrollHeight
  const canScrollUp = container.scrollTop > 0
  if ((event.deltaY > 0 && canScrollDown) || (event.deltaY < 0 && canScrollUp)) return

  event.preventDefault()
  container.scrollLeft += event.deltaY
}

const canEditAppraisal = (appraisal: CrewAppraisal) =>
  (isSuperAdmin || hasPermission('UPDATE_CREW_APPRAISAL')) && appraisal.workflowStatus === 'DRAFT'

const withResolvedDisplayFields = (appraisal: CrewAppraisal): CrewAppraisal => {
  const crew = crewMembers.find((c) => c.id === appraisal.crewId)
  const crewAny = crew as any
  const resolvedRankId = appraisal.rankId ?? crew?.rankId ?? crewAny?.rank?.id ?? null
  const resolvedRankNameFromRanks =
    resolvedRankId != null ? ranks.find((r) => r.id === resolvedRankId)?.rank : undefined
  const resolvedRankName =
    appraisal.rankName ??
    resolvedRankNameFromRanks ??
    crewAny?.rank?.rank ??
    null

  return {
    ...appraisal,
    crewName: appraisal.crewName || crew?.name || '-',
    rankId: resolvedRankId,
    rankName: resolvedRankName,
    appraisalType: (appraisal.appraisalType || DEFAULT_APPRAISAL_TYPE) as AppraisalType,
    averageReport: (appraisal.averageReport || DEFAULT_AVERAGE_REPORT) as AverageReport,
    remarks: appraisal.remarks ?? '',
  }
}

const handleNewAppraisal = () => {
  const defaultCrew = !isCrew || isCrewMaster ? null : selfCrewMember ?? null
  const defaultCrewName = defaultCrew?.name ?? (isCrew && !isCrewMaster ? currentCrewName : '')
  const defaultRankId = defaultCrew?.rankId ?? (isCrew && !isCrewMaster ? (crewRankId || null) : null)
  const defaultRankName =
    (defaultRankId ? ranks.find((rank) => rank.id === defaultRankId)?.rank : undefined) ??
    (isCrew && !isCrewMaster ? (currentUser as any)?.rank?.rank : undefined) ??
    null

  setEditingAppraisal({
    id: 0,
    srNo: appraisals.length + 1,
    crewId: defaultCrew?.id ?? (isCrew && !isCrewMaster ? currentCrewId : 0),
    crewName: defaultCrewName,
    vesselId: defaultCrew?.vessel?.id ?? defaultCrew?.vesselId ?? (isCrew && !isCrewMaster ? currentUser?.vessel?.id ?? null : null),
    vesselName: defaultCrew?.vessel?.fleet_name ?? (isCrew && !isCrewMaster ? currentUser?.vessel?.fleet_name ?? null : null),
    rankId: defaultRankId,
    rankName: defaultRankName,
    appraisalType: 'MID_TERM',
    averageReport: 'AVERAGE',
    remarks: '',
    officeRemarks: null,
    workflowStatus: 'DRAFT',
    submittedAt: null,
    reviewedAt: null,
    reviewedByName: null,
    attachments: [],
    attachmentCount: 0,
    createdAt: '',
    updatedAt: '',
  })
  setIsNewAppraisal(true)
  setIsModalOpen(true)
}

const handleSaveAppraisal = async (pendingFiles: PendingAppraisalAttachment[]) => {
  if (!editingAppraisal) return
  if (!editingAppraisal.crewId) {
    toast.error('Crew member is required')
    return
  }
  if (!editingAppraisal.rankId) {
    toast.error('Rank is required')
    return
  }
  if (!editingAppraisal.appraisalType) {
    toast.error('Appraisal type is required')
    return
  }
  if (!editingAppraisal.averageReport) {
    toast.error('Average report is required')
    return
  }

  setIsSaving(true)
  try {
    if (isNewAppraisal) {
      const payload: CreateCrewAppraisalRequest = {
        crewId: editingAppraisal.crewId,
        vesselId: editingAppraisal.vesselId || undefined,
        rankId: editingAppraisal.rankId || undefined,
        appraisalType: editingAppraisal.appraisalType,
        averageReport: editingAppraisal.averageReport,
        remarks: editingAppraisal.remarks || '',
      }
      const created = await createCrewAppraisal(payload)
      for (const item of pendingFiles || []) {
        await uploadCrewAppraisalAttachment(created.id, item.attachmentType, item.file)
      }
      toast.success('Appraisal created successfully')
    } else {
      const payload: UpdateCrewAppraisalRequest = {
        rankId: editingAppraisal.rankId || undefined,
        appraisalType: editingAppraisal.appraisalType,
        averageReport: editingAppraisal.averageReport,
        remarks: editingAppraisal.remarks || '',
      }
      await updateCrewAppraisal(editingAppraisal.id, payload)
      toast.success('Appraisal updated successfully')
    }
    setIsModalOpen(false)
    setEditingAppraisal(null)
    setIsNewAppraisal(false)
    await fetchAppraisals()
  } catch (error) {
    console.error(error)
    showErrorToast(error, 'Failed to save appraisal', isNewAppraisal ? 'create' : 'update')
  } finally {
    setIsSaving(false)
  }
}

const handleSubmit = async (appraisal: CrewAppraisal) => {
  if (!canSubmitAppraisal) {
    toast.error('You do not have permission to submit appraisals')
    return
  }
  try {
    await submitCrewAppraisal(appraisal.id)
    toast.success('Appraisal submitted for review')
    await fetchAppraisals()
  } catch (error) {
    console.error(error)
    showErrorToast(error, 'Failed to submit appraisal', 'save')
  }
}

const deleteAppraisalNow = async (appraisal: CrewAppraisal) => {
  try {
    await deleteCrewAppraisal(appraisal.id)
    toast.success('Draft appraisal deleted')
    await fetchAppraisals()
  } catch (error) {
    console.error(error)
    showErrorToast(error, 'Failed to delete appraisal', 'delete')
  }
}

const handleDeleteDraft = (appraisal: CrewAppraisal) => {
  const toastId = `delete-appraisal-${appraisal.id}`
  if (toast.isActive(toastId)) return
  toast.info(
    ({ closeToast }) => (
      <div>
        <div className='mb-2'>Delete draft appraisal for {appraisal.crewName}?</div>
        <div className='d-flex gap-2'>
          <button
            className='btn btn-sm btn-danger'
            onClick={async () => {
              closeToast?.()
              await deleteAppraisalNow(appraisal)
            }}
          >
            Delete
          </button>
          <button className='btn btn-sm btn-light' onClick={() => closeToast?.()}>
            Cancel
          </button>
        </div>
      </div>
    ),
    {
      toastId,
      autoClose: false,
      closeOnClick: false,
    }
  )
}

const handleDepartmentReview = async (department: ReviewDepartment, remarks: string, rating: number) => {
  if (!reviewingAppraisal) return
  setIsReviewSaving(true)
  try {
    const updated = await submitDepartmentReview(reviewingAppraisal.id, { department, remarks, rating })
    setReviewingAppraisal(updated)
    toast.success(`${department.charAt(0) + department.slice(1).toLowerCase()} review submitted successfully`)
    if (updated.workflowStatus === 'REVIEWED') {
      toast.success('All 4 department reviews complete — Appraisal approved!')
      setIsReviewModalOpen(false)
      setReviewingAppraisal(null)
    }
    await fetchAppraisals()
  } catch (error) {
    console.error(error)
    showErrorToast(error, 'Failed to submit department review', 'save')
  } finally {
    setIsReviewSaving(false)
  }
}

const handleUploadExistingAttachment = async (attachmentType: AppraisalAttachmentType, files: FileList | null) => {
  if (!editingAppraisal || !editingAppraisal.id || !files || files.length === 0) return
  try {
    let latest: CrewAppraisal | null = null
    for (const file of Array.from(files)) {
      latest = await uploadCrewAppraisalAttachment(editingAppraisal.id, attachmentType, file)
    }
    if (latest) setEditingAppraisal(latest)
    await fetchAppraisals()
    toast.success('Attachment uploaded successfully')
  } catch (error) {
    console.error(error)
    showErrorToast(error, 'Failed to upload attachment', 'update')
  }
}

const renderStatusBadge = (status: AppraisalWorkflowStatus) => {
  if (status === 'DRAFT') return <span className='badge badge-light-secondary'>Draft</span>
  if (status === 'SUBMITTED') return <span className='badge badge-light-warning'>Submitted</span>
  return <span className='badge badge-light-success'>Reviewed</span>
}

const renderActions = (appraisal: CrewAppraisal) => (
  <div className='d-flex align-items-center gap-1'>
    <button className='btn btn-icon btn-sm' title='View' onClick={() => { setViewAppraisal(withResolvedDisplayFields(appraisal)); setIsViewModalOpen(true) }}>
      <KTSVG path='/media/map/ph_eye.svg' className='svg-icon-3 text-primary' />
    </button>

    {canEditAppraisal(appraisal) && (
      <button className='btn btn-icon btn-sm' title='Edit' onClick={() => { setEditingAppraisal(withResolvedDisplayFields(appraisal)); setIsNewAppraisal(false); setIsModalOpen(true) }}>
        <KTSVG path='/media/map/edit-active.svg' className='svg-icon-3' />
      </button>
    )}

    {canDelete && appraisal.workflowStatus === 'DRAFT' && (
      <button className='btn btn-icon btn-sm' title='Delete' onClick={() => handleDeleteDraft(appraisal)}>
        <KTSVG path='/media/map/red-cross-icon.svg' className='svg-icon-3' />
      </button>
    )}

    {canSubmitAppraisal && appraisal.workflowStatus === 'DRAFT' && (
      <button type='button' className='btn btn-light-primary btn-sm' onClick={() => handleSubmit(appraisal)}>Submit</button>
    )}

    {canReview && appraisal.workflowStatus === 'SUBMITTED' && (
      <button type='button' className='btn btn-light-warning btn-sm' onClick={() => { setReviewingAppraisal(appraisal); setIsReviewModalOpen(true) }}>Review</button>
    )}
  </div>
)

return (
  <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
    <div className='d-flex flex-column flex-column-fluid'>
      <div id='kt_app_content' className='app-content flex-column-fluid bg-white'>
        <div className='card'>
          {/* Reviewed appraisal alerts for master-created appraisals */}
          {masterReviewedAlerts.map((a) => (
            <div key={a.id} className='alert alert-success d-flex align-items-center justify-content-between mx-5 mt-4 mb-0 py-3 px-4' role='alert'>
              <div className='d-flex align-items-center'>
                <span className='me-2' style={{ fontSize: 18 }}>&#10003;</span>
                <span className='text-dark'>
                  Your appraisal <strong>{APPRAISAL_TYPE_LABEL[a.appraisalType] || a.appraisalType}</strong> has been reviewed by all departments.
                </span>
              </div>
              <button
                type='button'
                className='btn-close'
                aria-label='Close'
                onClick={() => {
                  const updated = new Set(dismissedAlertIds)
                  updated.add(a.id!)
                  setDismissedAlertIds(updated)
                  try { localStorage.setItem(masterDismissedAlertsStorageKey, JSON.stringify(Array.from(updated))) } catch { }
                }}
              />
            </div>
          ))}

          {/* Reviewer alerts for submitted appraisals */}
          {reviewerPendingAlerts.map((a) => {
            const reviewCount = Number(a.reviewCount ?? 0)
            const remaining = Math.max(4 - reviewCount, 0)
            const isFresh = reviewCount === 0

            return (
              <div
                key={`reviewer-${a.id}`}
                className={`alert d-flex align-items-center justify-content-between mx-5 mt-4 mb-0 py-3 px-4 ${isFresh ? 'alert-warning' : 'alert-info'
                  }`}
                role='alert'
              >
                <div className='d-flex align-items-center'>
                  <span className='me-2' style={{ fontSize: 18 }}>
                    {isFresh ? '⚠' : 'ℹ'}
                  </span>
                  <span className='text-dark'>
                    {isFresh
                      ? <>A new appraisal for <strong>{a.crewName || 'crew'}</strong> is awaiting review.</>
                      : <>Appraisal for <strong>{a.crewName || 'crew'}</strong> still has <strong>{remaining}</strong> review(s) pending.</>}
                  </span>
                </div>
                <button
                  type='button'
                  className='btn-close'
                  aria-label='Close'
                  onClick={() => {
                    const updated = new Set(dismissedReviewerAlertIds)
                    updated.add(a.id!)
                    setDismissedReviewerAlertIds(updated)
                    try {
                      sessionStorage.setItem('dismissedReviewerAppraisalAlerts', JSON.stringify(Array.from(updated)))
                    } catch { }
                  }}
                />
              </div>
            )
          })}
          <div className='card-header border-0 pt-6 d-flex justify-content-between bg-white'>
            <div>
              <h3 className='card-label text-dark fw-bold'>Crew Appraisal</h3>
              <p className='text-muted mb-0 mt-1'>Create, submit and review crew appraisal records with attachments.</p>
            </div>
            <div className='card-toolbar'>
              {canCreate && (
                <button
                  type='button'
                  className='btn text-white fw-bold shadow-sm d-inline-flex align-items-center gap-2'
                  style={{
                    backgroundColor: 'rgb(29, 78, 216)',
                    borderColor: 'rgb(29, 78, 216)',
                    borderRadius: '9.75px',
                    padding: '8px 13px',
                    fontSize: '11.5px',
                    fontWeight: 700,
                    boxShadow: 'rgba(0, 0, 0, 0.08) 0px 2px 8px',
                    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                    gap: '6.5px',
                  }}
                  onClick={handleNewAppraisal}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='14'
                    height='14'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    style={{ width: '13px', height: '13px' }}
                  >
                    <line x1='12' y1='5' x2='12' y2='19'></line>
                    <line x1='5' y1='12' x2='19' y2='12'></line>
                  </svg>
                  Add Appraisal
                </button>
              )}
            </div>
          </div>

          <div className='card-body py-4 bg-white border-top'>
            <div className='row gx-3 gy-3'>
              <div className='col-md-3'>
                <label className='form-label fw-semibold fs-7' style={{ color: '#A1A5B7' }}>{useCrewDropdownFilter ? 'Crew' : 'Search'}</label>
                {useCrewDropdownFilter ? (
                  <select className='form-select' value={selectedCrewId} onChange={(e) => setSelectedCrewId(e.target.value ? Number(e.target.value) : '')}>
                    <option value=''>All Crew</option>
                    {crewFilterOptions.map((crew) => (
                      <option key={crew.id} value={crew.id}>{crew.name}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type='text'
                    className='form-control'
                    placeholder='Search crew or rank'
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                )}
              </div>

              <div className='col-md-2'>
                <label className='form-label fw-semibold fs-7' style={{ color: '#A1A5B7' }}>Status</label>
                <select className='form-select' value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as 'All' | AppraisalWorkflowStatus)}>
                  <option value='All'>All Status</option>
                  <option value='DRAFT'>Draft</option>
                  <option value='SUBMITTED'>Submitted</option>
                  <option value='REVIEWED'>Reviewed</option>
                </select>
              </div>

              <div className='col-md-3'>
                <label className='form-label fw-semibold fs-7' style={{ color: '#A1A5B7' }}>Average Report</label>
                <select className='form-select' value={averageFilter} onChange={(e) => setAverageFilter(e.target.value as 'All' | AverageReport)}>
                  <option value='All'>All Average</option>
                  <option value='NTBE'>NTBE</option>
                  <option value='AVERAGE'>Average</option>
                  <option value='GOOD'>Good</option>
                  <option value='EXCELLENT'>Excellent</option>
                </select>
              </div>
            </div>
          </div>

          <div className='card-body py-4 bg-white border-top'>
            <div className='row g-3'>
              <div className='col'><div className='border rounded p-3'><div className='text-muted fs-7 fw-semibold'>Total Appraisals</div><div className='fs-3 fw-bold text-dark'>{totals.total}</div></div></div>
              <div className='col'><div className='border rounded p-3'><div className='text-muted fs-7 fw-semibold'>Submitted</div><div className='fs-3 fw-bold text-warning'>{totals.submitted}</div></div></div>
              <div className='col'><div className='border rounded p-3'><div className='text-muted fs-7 fw-semibold'>In Progress</div><div className='fs-3 fw-bold text-info'>{totals.inProgress}</div></div></div>
              <div className='col'><div className='border rounded p-3'><div className='text-muted fs-7 fw-semibold'>Reviewed</div><div className='fs-3 fw-bold text-success'>{totals.reviewed}</div></div></div>
              <div className='col'><div className='border rounded p-3'><div className='text-muted fs-7 fw-semibold'>NTBE</div><div className='fs-3 fw-bold text-danger'>{totals.ntbe}</div></div></div>
            </div>
          </div>

          <div className='card-body py-4 bg-white border-top'>
            <div className='report-table'>
              <div className='table-responsive' onWheel={handleTableHorizontalWheelScroll}>
                <table className='table table-bordered align-middle'>
                    <thead className='table-header text-start'>
                      <tr>
                        <th className='text-center text-nowrap' style={{ minWidth: '70px' }}>Sr/No</th>
                        <th style={{ minWidth: '180px' }}><div className='d-flex align-items-center text-nowrap'><span className='me-1'>Name</span><button type='button' className='btn btn-link p-0 m-0 pb-1' onClick={() => handleSort('crewName')}><KTSVG path={`/media/map/sort-col-${sortConfig.key === 'crewName' ? (sortConfig.direction === 'asc' ? 'up-black' : 'down-black') : 'grey'}.svg`} className='svg-icon-3' /></button></div></th>
                        <th style={{ minWidth: '140px' }}>Rank</th>
                        <th style={{ minWidth: '150px' }}>Appraisal Type</th>
                        <th style={{ minWidth: '140px' }}>Average Report</th>
                        <th style={{ minWidth: '140px' }}>Attachments</th>
                        <th style={{ minWidth: '160px', maxWidth: '220px' }}>Remarks</th>
                        <th style={{ minWidth: '160px', maxWidth: '220px' }}>Operation Remarks</th>
                        <th style={{ minWidth: '160px', maxWidth: '220px' }}>Technical Remarks</th>
                        <th style={{ minWidth: '160px', maxWidth: '220px' }}>QHSC Remarks</th>
                        <th style={{ minWidth: '160px', maxWidth: '220px' }}>Crewing Remarks</th>
                        <th style={{ minWidth: '120px' }}>Reviews</th>
                        <th style={{ minWidth: '120px' }}>Status</th>
                        <th style={{ minWidth: '230px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading ? (
                        <TableSkeleton rows={rowsPerPage || 10} cols={14} />
                      ) : currentRows.length === 0 ? (
                        <tr><td colSpan={14} className='text-center text-muted py-4'>No crew appraisal data found.</td></tr>
                      ) : (
                        currentRows.map((row, idx) => {
                          const resolvedRow = withResolvedDisplayFields(row)
                          return (
                            <tr key={resolvedRow.id || idx}>
                              <td className='text-center'>{indexOfFirst + idx + 1}</td>
                              <td>{resolvedRow.crewName || '-'}</td>
                              <td>{resolvedRow.rankName || '-'}</td>
                              <td>{APPRAISAL_TYPE_LABEL[resolvedRow.appraisalType] || resolvedRow.appraisalType}</td>
                              <td><span className={`badge ${AVERAGE_BADGE_CLASS[resolvedRow.averageReport] || 'badge-light'}`}>{AVERAGE_REPORT_LABEL[resolvedRow.averageReport] || resolvedRow.averageReport}</span></td>
                              <td>
                                <button
                                  type='button'
                                  className='btn btn-sm btn-light-primary text-nowrap'
                                  style={{ whiteSpace: 'nowrap' }}
                                  onClick={() => setAttachmentsModal({ visible: true, appraisal: resolvedRow })}
                                >
                                  View Files ({resolvedRow.attachmentCount || resolvedRow.attachments?.length || 0})
                                </button>
                              </td>
                              <td>
                                <RemarksCell
                                  text={resolvedRow.remarks}
                                  title='Appraisal Remarks'
                                  subtitle={resolvedRow.crewName || ''}
                                  onOpenModal={(title, subtitle, text) => {
                                    setRemarksModal({ visible: true, title, subtitle, text })
                                  }}
                                />
                              </td>
                              <td>
                                <RemarksCell
                                  text={resolvedRow.operationRemarks}
                                  title='Operation Remarks'
                                  subtitle={resolvedRow.crewName || ''}
                                  onOpenModal={(title, subtitle, text) => {
                                    setRemarksModal({ visible: true, title, subtitle, text })
                                  }}
                                />
                              </td>
                              <td>
                                <RemarksCell
                                  text={resolvedRow.technicalRemarks}
                                  title='Technical Remarks'
                                  subtitle={resolvedRow.crewName || ''}
                                  onOpenModal={(title, subtitle, text) => {
                                    setRemarksModal({ visible: true, title, subtitle, text })
                                  }}
                                />
                              </td>
                              <td>
                                <RemarksCell
                                  text={resolvedRow.qhscRemarks}
                                  title='QHSC Remarks'
                                  subtitle={resolvedRow.crewName || ''}
                                  onOpenModal={(title, subtitle, text) => {
                                    setRemarksModal({ visible: true, title, subtitle, text })
                                  }}
                                />
                              </td>
                              <td>
                                <RemarksCell
                                  text={resolvedRow.crewingRemarks}
                                  title='Crewing Remarks'
                                  subtitle={resolvedRow.crewName || ''}
                                  onOpenModal={(title, subtitle, text) => {
                                    setRemarksModal({ visible: true, title, subtitle, text })
                                  }}
                                />
                              </td>
                              <td>
                                <span className={`badge ${(resolvedRow.reviewCount ?? 0) >= 4 ? 'badge-light-success' : (resolvedRow.reviewCount ?? 0) > 0 ? 'badge-light-info' : 'badge-light-secondary'}`}>
                                  {resolvedRow.reviewCount ?? 0}/4
                                </span>
                              </td>
                              <td>{renderStatusBadge(resolvedRow.workflowStatus)}</td>
                              <td>{renderActions(resolvedRow)}</td>
                            </tr>
                          )
                        })
                      )}
                    </tbody>
                  </table>
                </div>

              <div className='d-flex justify-content-between align-items-center py-4 px-2 border-top'>
                <div className='d-flex align-items-center gap-3'>
                  <span className='text-muted mb-0'>Rows per page</span>
                  <select
                    className='form-select form-select-sm rounded-pill'
                    style={{ width: 92 }}
                    value={rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(Number(e.target.value))
                      setCurrentPage(1)
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>

                <div className='d-flex align-items-center gap-3'>
                  <span className='text-muted small'>
                    Showing <strong>{sortedAppraisals.length ? indexOfFirst + 1 : 0}-{Math.min(indexOfLast, sortedAppraisals.length)}</strong> of <strong>{sortedAppraisals.length}</strong>
                  </span>
                  <div className='d-flex align-items-center gap-2'>
                    <button
                      type='button'
                      className='btn btn-light btn-sm'
                      style={{ minWidth: 40, height: 40 }}
                      disabled={currentPage <= 1}
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    >
                      ‹
                    </button>
                    <button
                      type='button'
                      className='btn btn-light btn-sm'
                      style={{ minWidth: 40, height: 40 }}
                      disabled
                    >
                      {Math.max(1, currentPage)}
                    </button>
                    <button
                      type='button'
                      className='btn btn-light btn-sm'
                      style={{ minWidth: 40, height: 40 }}
                      disabled={currentPage >= Math.max(1, totalPages)}
                      onClick={() => setCurrentPage((p) => Math.min(Math.max(1, totalPages), p + 1))}
                    >
                      ›
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <CrewAppraisalModal
      visible={isModalOpen}
      appraisal={editingAppraisal}
      isNew={isNewAppraisal}
      lockCrewSelection={isCrew && !isCrewMaster}
      isSuperAdmin={isSuperAdmin}
      isSaving={isSaving}
      crewMembers={selectableCrewMembers}
      ranks={ranks}
      companyGroups={companyAdmins}
      subCompanies={companies}
      vessels={effectiveVessels}
      onClose={() => { setIsModalOpen(false); setEditingAppraisal(null); setIsNewAppraisal(false) }}
      onChange={setEditingAppraisal}
      onSave={handleSaveAppraisal}
      onUploadExistingAttachment={handleUploadExistingAttachment}
      onAttachmentRemoved={(next) => { setEditingAppraisal(next); fetchAppraisals() }}
    />

    <ViewCrewAppraisalModal
      visible={isViewModalOpen}
      appraisal={viewAppraisal}
      onClose={() => { setIsViewModalOpen(false); setViewAppraisal(null) }}
    />

    <CrewAppraisalAttachmentsModal
      visible={attachmentsModal.visible}
      appraisal={attachmentsModal.appraisal}
      onClose={() => setAttachmentsModal({ visible: false, appraisal: null })}
      onViewAttachment={({ title, fileName, viewUrl, downloadUrl }) => setFileModal({ visible: true, title, fileName, viewUrl, downloadUrl })}
    />

    <CrewAppraisalReviewModal
      visible={isReviewModalOpen}
      appraisal={reviewingAppraisal}
      isLoading={isReviewSaving}
      onClose={() => { setIsReviewModalOpen(false); setReviewingAppraisal(null) }}
      onSubmitDepartmentReview={handleDepartmentReview}
    />

    <FileViewerModal
      visible={fileModal.visible}
      title={fileModal.title}
      fileName={fileModal.fileName}
      viewUrl={fileModal.viewUrl}
      downloadUrl={fileModal.downloadUrl}
      onClose={() => setFileModal((prev) => ({ ...prev, visible: false }))}
    />

    <RemarksModal
      visible={remarksModal.visible}
      title={remarksModal.title}
      subtitle={remarksModal.subtitle}
      remarksText={remarksModal.text}
      onClose={() => setRemarksModal((prev) => ({ ...prev, visible: false }))}
    />

    <ToastContainer
      position='top-center'
      autoClose={3000}
      hideProgressBar={false}
      newestOnTop={false}
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      style={{ top: '5rem' }}
    />
  </div>
)
}

export default Appraisal
